<template>
  <div class="dts-support">
    <!-- Government banner -->
    <div class="topbar">
      Official website of the Commission on Higher Education (CHED) &mdash; CARAGA Region &mdash; Republic of the Philippines
    </div>

    <!-- Sticky navbar -->
    <header class="site-header">
      <div class="nav-inner">
        <a href="#" class="nav-brand">
          <img src="@/assets/logo/ched.png" alt="CHED Logo" class="brand-logo" />
          <div class="brand-text">
            <strong>CHED DTS</strong>
            <span>Document Tracking System</span>
          </div>
        </a>

        <button class="nav-toggle" aria-label="Toggle navigation" @click="menuOpen = !menuOpen">
          <span></span><span></span><span></span>
        </button>

        <nav :class="{ open: menuOpen }">
          <a href="#support" @click="menuOpen = false">App Support</a>
          <a href="#privacy" @click="menuOpen = false">Privacy Policy</a>
          <a href="#terms" @click="menuOpen = false">Terms of Use</a>
        </nav>
      </div>
    </header>

    <!-- Hero -->
    <section class="hero">
      <div class="hero-badge">Support &amp; Legal</div>
      <h1>CHED DTS<br>Document Tracking System</h1>
      <p>Official support, privacy policy, and terms of use for the CHED DTS mobile application.</p>
    </section>

    <!-- Main content -->
    <main>

      <!-- APP SUPPORT -->
      <section id="support">
        <div class="section-header">
          <div class="section-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
              <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z"/>
              <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
            </svg>
          </div>
          <h2>App Support</h2>
        </div>
        <div class="section-divider"></div>

        <div class="card">
          <p>
            The <strong>CHED Document Tracking System (DTS)</strong> is an official mobile application developed by the
            Commission on Higher Education to streamline the routing, monitoring, and management of documents across CHED
            regional and central offices. The app enables staff to track document status in real time, receive
            notifications on pending actions, forward or acknowledge documents, and maintain an auditable digital paper
            trail &mdash; reducing reliance on physical routing slips and manual follow-ups.
          </p>
          <p>
            If you are experiencing technical issues, have questions about your account, or need help understanding a
            feature, our support team is ready to assist you. Please reach out using the contact information below or
            submit a message through the form.
          </p>

          <div class="contact-strip">
            <svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            For support, email us at&nbsp;<a href="mailto:ccalinawan@ched.gov.ph">ccalinawan@ched.gov.ph</a>
          </div>

          <div v-if="formSuccess" class="alert-success-strip">
            <svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            Your message has been sent. We'll get back to you at {{ form.email }}.
          </div>
          <div v-if="formError" class="alert-error-strip">
            <svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {{ formError }}
          </div>

          <form @submit.prevent="handleSubmit">
            <div class="form-grid">
              <div class="form-group">
                <label for="name">Full Name</label>
                <input v-model="form.name" type="text" id="name" placeholder="Juan dela Cruz" required />
              </div>
              <div class="form-group">
                <label for="email">Email Address</label>
                <input v-model="form.email" type="email" id="email" placeholder="you@example.gov.ph" required />
              </div>
              <div class="form-group full">
                <label for="message">Message</label>
                <textarea v-model="form.message" id="message" placeholder="Describe your issue or question in detail…" required></textarea>
              </div>
            </div>
            <button type="submit" class="btn-submit" :disabled="sending">
              <svg v-if="!sending" viewBox="0 0 24 24" fill="none" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="spin">
                <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
              </svg>
              {{ sending ? 'Sending…' : 'Send Message' }}
            </button>
          </form>
        </div>
      </section>

      <!-- PRIVACY POLICY -->
      <section id="privacy">
        <div class="section-header">
          <div class="section-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          <h2>Privacy Policy</h2>
        </div>
        <div class="section-divider"></div>

        <div class="card">
          <span class="effective-date">Effective Date: January 1, 2026</span>
          <div class="prose">
            <p>
              The Commission on Higher Education (CHED) is committed to protecting your privacy. This Privacy Policy
              describes how the CHED Document Tracking System (DTS) mobile application collects, uses, and safeguards
              your personal information in compliance with Republic Act No. 10173, otherwise known as the
              <strong>Data Privacy Act of 2012</strong>.
            </p>

            <h3>1. Information We Collect</h3>
            <p>We collect only the information necessary to operate the DTS application effectively:</p>
            <ul>
              <li><strong>Account Information:</strong> Full name, official email address, office assignment, designation, and role within the CHED system.</li>
              <li><strong>Activity Data:</strong> Document routing actions, timestamps, acknowledgment records, and audit trail entries you create within the system.</li>
              <li><strong>Device Information:</strong> Device type, operating system version, and push notification token (used solely for delivering in-app notifications).</li>
              <li><strong>Usage Data:</strong> App interaction logs for performance monitoring and debugging purposes.</li>
            </ul>

            <h3>2. How We Use Your Information</h3>
            <ul>
              <li>To authenticate your identity and manage your account access.</li>
              <li>To process and route documents within the CHED office network.</li>
              <li>To send notifications about documents requiring your attention or action.</li>
              <li>To maintain a complete, tamper-evident audit trail for all document transactions.</li>
              <li>To generate statistical and operational reports for CHED management.</li>
              <li>To diagnose technical issues and improve application performance.</li>
            </ul>

            <h3>3. Data Sharing and Disclosure</h3>
            <p>
              Your personal data is accessible only to authorized CHED personnel in the performance of their official
              duties. We do not sell, rent, or share your personal information with third parties for commercial
              purposes. Data may be disclosed in the following circumstances:
            </p>
            <ul>
              <li>As required by applicable law, court order, or lawful government directive.</li>
              <li>To CHED's authorized ICT service providers who assist in operating and maintaining the system, subject to strict data processing agreements.</li>
              <li>As necessary for the investigation of security incidents or violations of these policies.</li>
            </ul>

            <h3>4. Data Retention</h3>
            <p>
              Personal data and document transaction records are retained in accordance with the CHED Records Retention
              Schedule and applicable government archiving regulations. Account data is retained for the duration of
              your employment or engagement with CHED and for a reasonable period thereafter for audit purposes.
            </p>

            <h3>5. Data Security</h3>
            <p>
              We implement appropriate technical and organizational measures to protect your personal data against
              unauthorized access, disclosure, alteration, and destruction. These include encrypted data transmission
              (TLS/HTTPS), role-based access controls, and regular security assessments. However, no system is entirely
              immune to security risks; we encourage you to protect your login credentials and report any suspected
              breaches immediately.
            </p>

            <h3>6. Your Rights as a Data Subject</h3>
            <p>Under the Data Privacy Act of 2012, you have the right to:</p>
            <ul>
              <li><strong>Access</strong> your personal data held by CHED DTS.</li>
              <li><strong>Correct</strong> inaccurate or incomplete personal data.</li>
              <li><strong>Erasure or blocking</strong> of data processed unlawfully.</li>
              <li><strong>Object</strong> to the processing of your personal data.</li>
              <li><strong>File a complaint</strong> before the National Privacy Commission.</li>
            </ul>
            <p>
              To exercise these rights, contact your agency Data Protection Officer or email us at
              <a href="mailto:ccalinawan@ched.gov.ph">ccalinawan@ched.gov.ph</a>.
            </p>

            <h3>7. Cookies and Local Storage</h3>
            <p>
              The DTS mobile application may store a session token and user preference data locally on your device to
              maintain your authenticated session and app settings. This information is not shared with third parties
              and can be cleared by logging out or uninstalling the application.
            </p>

            <h3>8. Changes to This Policy</h3>
            <p>
              CHED reserves the right to update this Privacy Policy periodically to reflect changes in legal
              requirements or application features. Material changes will be communicated through an in-app
              notification. Continued use of the application after such changes constitutes acceptance of the
              updated policy.
            </p>

            <h3>9. Contact</h3>
            <p>
              For privacy-related concerns, please contact the CHED Data Protection Officer or reach out via
              <a href="mailto:ccalinawan@ched.gov.ph">ccalinawan@ched.gov.ph</a>.
            </p>
          </div>
        </div>
      </section>

      <!-- TERMS OF USE -->
      <section id="terms">
        <div class="section-header">
          <div class="section-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
          </div>
          <h2>Terms of Use</h2>
        </div>
        <div class="section-divider"></div>

        <div class="card">
          <span class="effective-date">Effective Date: January 1, 2026</span>
          <div class="prose">
            <p>
              These Terms of Use govern your access to and use of the CHED Document Tracking System (DTS) mobile
              application, operated by the Commission on Higher Education (CHED) of the Republic of the Philippines.
              By accessing or using the application, you agree to be bound by these terms.
            </p>

            <h3>1. Authorized Users</h3>
            <p>
              The CHED DTS application is intended exclusively for authorized CHED personnel, including regular
              employees, contract-of-service staff, and other individuals who have been granted official system access
              by a CHED administrator. Unauthorized access is strictly prohibited and may be subject to civil and
              criminal penalties under applicable law.
            </p>

            <h3>2. Account Responsibilities</h3>
            <ul>
              <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
              <li>You must not share your account with any other person.</li>
              <li>You must immediately notify your system administrator or email <a href="mailto:ccalinawan@ched.gov.ph">ccalinawan@ched.gov.ph</a> if you suspect unauthorized use of your account.</li>
              <li>CHED shall not be liable for any loss arising from your failure to safeguard your credentials.</li>
            </ul>

            <h3>3. Acceptable Use</h3>
            <p>You agree to use the CHED DTS application only for lawful purposes and in accordance with its intended function. You must not:</p>
            <ul>
              <li>Use the application for any purpose unrelated to official CHED business.</li>
              <li>Attempt to gain unauthorized access to any part of the system or its underlying infrastructure.</li>
              <li>Introduce malware, viruses, or any other harmful code into the system.</li>
              <li>Alter, delete, or tamper with documents or audit records in the system.</li>
              <li>Use the application in any way that could damage, disable, or impair the system or interfere with other users.</li>
              <li>Circumvent, disable, or attempt to bypass any security or access control mechanism.</li>
            </ul>

            <h3>4. Integrity of Document Records</h3>
            <p>
              All actions performed through the DTS &mdash; including forwarding, acknowledging, releasing, and
              annotating documents &mdash; are permanently logged with timestamps and user identifiers. Users
              acknowledge that these records constitute an official government audit trail and carry the same weight
              as physical document routing records. Falsification of document entries may constitute a violation of
              Republic Act No. 6713 (Code of Conduct and Ethical Standards for Public Officials and Employees) and
              other applicable laws.
            </p>

            <h3>5. Intellectual Property</h3>
            <p>
              The CHED DTS application, including its design, source code, and content, is the property of the
              Commission on Higher Education. All rights are reserved. You may not copy, reproduce, distribute, or
              create derivative works from any portion of the application without prior written authorization from CHED.
            </p>

            <h3>6. Disclaimer of Warranties</h3>
            <p>
              The CHED DTS application is provided on an "as is" and "as available" basis. While CHED strives to
              ensure continuous availability and data accuracy, we do not warrant that the application will be
              uninterrupted, error-free, or free from security vulnerabilities. CHED shall not be liable for any
              direct, indirect, incidental, or consequential damages arising from the use of or inability to use
              the application.
            </p>

            <h3>7. Termination of Access</h3>
            <p>
              CHED reserves the right to suspend or terminate your access to the application at any time, with or
              without notice, for any violation of these Terms of Use, upon separation from service, or for any
              other reason deemed necessary by CHED management.
            </p>

            <h3>8. Governing Law</h3>
            <p>
              These Terms of Use shall be governed by the laws of the Republic of the Philippines. Any dispute
              arising from or in connection with the use of this application shall be subject to the exclusive
              jurisdiction of the appropriate courts in the Philippines.
            </p>

            <h3>9. Amendments</h3>
            <p>
              CHED may revise these Terms of Use at any time. Continued use of the application after the posting of
              changes constitutes your acceptance of the revised terms. Users will be notified of significant changes
              via in-app notifications.
            </p>

            <h3>10. Contact</h3>
            <p>
              For questions regarding these Terms of Use, please contact
              <a href="mailto:ccalinawan@ched.gov.ph">ccalinawan@ched.gov.ph</a>.
            </p>
          </div>
        </div>
      </section>

    </main>

    <!-- Footer -->
    <footer>
      <div class="footer-logo">CHED Document Tracking System (DTS)</div>
      <div>Commission on Higher Education &mdash; Republic of the Philippines</div>
      <div class="footer-divider"></div>
      <div>&copy; 2026 Commission on Higher Education (CHED). All rights reserved.</div>
      <div class="footer-email">Support: <a href="mailto:ccalinawan@ched.gov.ph">ccalinawan@ched.gov.ph</a></div>
      <div class="footer-links">
        <a href="#support">App Support</a>
        <a href="#privacy">Privacy Policy</a>
        <a href="#terms">Terms of Use</a>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: "DtsSupport",
  data() {
    return {
      menuOpen: false,
      sending: false,
      formSuccess: false,
      formError: null,
      form: { name: "", email: "", message: "" },
    };
  },
  mounted() {
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.onScroll();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
  },
  methods: {
    handleSubmit() {
      this.sending = true;
      this.formSuccess = false;
      this.formError = null;

      const now = new Date().toLocaleString("en-PH", {
        timeZone: "Asia/Manila",
        dateStyle: "long",
        timeStyle: "short",
      });

      const body = `
<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/></head>
<body style="margin:0;padding:0;background:#f1f5f9;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f1f5f9;padding:32px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(13,43,94,.10);">

        <!-- Header -->
        <tr>
          <td style="background:linear-gradient(135deg,#091d40 0%,#1a3a72 100%);padding:32px 40px;text-align:center;">
            <div style="display:inline-block;background:#c8972b;border-radius:50%;width:52px;height:52px;line-height:52px;text-align:center;font-weight:800;font-size:14px;color:#091d40;letter-spacing:-0.5px;margin-bottom:14px;">DTS</div>
            <h1 style="margin:0;color:#ffffff;font-size:20px;font-weight:700;letter-spacing:-0.02em;">CHED DTS App Support</h1>
            <p style="margin:6px 0 0;color:rgba(255,255,255,.65);font-size:13px;">Commission on Higher Education — CARAGA Region</p>
          </td>
        </tr>

        <!-- Badge -->
        <tr>
          <td style="background:#fff7ed;border-bottom:1px solid #fed7aa;padding:10px 40px;text-align:center;">
            <span style="font-size:12px;font-weight:600;color:#c2410c;letter-spacing:.06em;text-transform:uppercase;">&#128276; New Support Request</span>
          </td>
        </tr>

        <!-- Body -->
        <tr>
          <td style="padding:32px 40px;">

            <p style="margin:0 0 24px;color:#475569;font-size:14px;line-height:1.6;">
              A new support request has been submitted through the <strong style="color:#0d2b5e;">CHED DTS mobile app support page</strong>. Details are below.
            </p>

            <!-- Info table -->
            <table width="100%" cellpadding="0" cellspacing="0" style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin-bottom:24px;">
              <tr>
                <td style="padding:12px 16px;border-bottom:1px solid #e2e8f0;width:30%;">
                  <span style="font-size:11px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.06em;">Full Name</span>
                </td>
                <td style="padding:12px 16px;border-bottom:1px solid #e2e8f0;">
                  <span style="font-size:14px;font-weight:600;color:#1e293b;">${this.form.name}</span>
                </td>
              </tr>
              <tr>
                <td style="padding:12px 16px;border-bottom:1px solid #e2e8f0;">
                  <span style="font-size:11px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.06em;">Email</span>
                </td>
                <td style="padding:12px 16px;border-bottom:1px solid #e2e8f0;">
                  <a href="mailto:${this.form.email}" style="font-size:14px;color:#2563eb;text-decoration:none;">${this.form.email}</a>
                </td>
              </tr>
              <tr>
                <td style="padding:12px 16px;">
                  <span style="font-size:11px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.06em;">Date Sent</span>
                </td>
                <td style="padding:12px 16px;">
                  <span style="font-size:14px;color:#475569;">${now}</span>
                </td>
              </tr>
            </table>

            <!-- Message -->
            <p style="margin:0 0 8px;font-size:11px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.06em;">Message</p>
            <div style="background:#f8fafc;border:1px solid #e2e8f0;border-left:4px solid #2563eb;border-radius:0 8px 8px 0;padding:16px 20px;color:#1e293b;font-size:14px;line-height:1.7;white-space:pre-wrap;">${this.form.message}</div>

          </td>
        </tr>

        <!-- Reply CTA -->
        <tr>
          <td style="padding:0 40px 32px;text-align:center;">
            <a href="mailto:${this.form.email}?subject=Re: CHED DTS Support Request"
               style="display:inline-block;background:#0d2b5e;color:#ffffff;text-decoration:none;font-size:14px;font-weight:600;padding:12px 28px;border-radius:6px;">
              &#9993; Reply to ${this.form.name}
            </a>
          </td>
        </tr>

        <!-- Footer -->
        <tr>
          <td style="background:#f8fafc;border-top:1px solid #e2e8f0;padding:20px 40px;text-align:center;">
            <p style="margin:0;font-size:12px;color:#94a3b8;line-height:1.7;">
              This message was sent via the <strong style="color:#475569;">CHED DTS App Support Page</strong>.<br/>
              &copy; 2026 Commission on Higher Education (CHED) — CARAGA Region
            </p>
          </td>
        </tr>

      </table>
    </td></tr>
  </table>
</body>
</html>`;

      const payload = {
        to: ["ccalinawan@ched.gov.ph"],
        cc: [this.form.email],
        subject: `[DTS Support] ${this.form.name} — ${this.form.message.substring(0, 60)}${this.form.message.length > 60 ? "…" : ""}`,
        body,
        document_ids: [],
      };

      axios.post("api/documents/send-email", payload)
        .then(() => {
          this.sending = false;
          this.formSuccess = true;
          this.form = { name: "", email: "", message: "" };
        })
        .catch((err) => {
          this.sending = false;
          this.formError =
            err.response?.data?.message || "Failed to send message. Please try again.";
        });
    },
    onScroll() {
      const sections = ["support", "privacy", "terms"];
      const scrollY = window.scrollY + 80;
      let active = sections[0];
      sections.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= scrollY) active = id;
      });
      this.$el.querySelectorAll("nav a").forEach((a) => {
        const id = a.getAttribute("href").replace("#", "");
        a.classList.toggle("active", id === active);
      });
    },
  },
};
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.dts-support {
  --navy:      #0d2b5e;
  --navy-dark: #091d40;
  --navy-mid:  #1a3a72;
  --accent:    #2563eb;
  --accent-lt: #dbeafe;
  --gold:      #c8972b;
  --white:     #ffffff;
  --gray-50:   #f8fafc;
  --gray-100:  #f1f5f9;
  --gray-200:  #e2e8f0;
  --gray-400:  #94a3b8;
  --gray-600:  #475569;
  --gray-800:  #1e293b;
  --radius:    6px;
  --shadow:    0 2px 12px rgba(13,43,94,.10);
  --shadow-lg: 0 8px 32px rgba(13,43,94,.14);

  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 16px;
  line-height: 1.7;
  color: var(--gray-800);
  background: var(--gray-50);
  min-height: 100vh;
}

/* TOP BAR */
.topbar {
  background: var(--navy-dark);
  color: rgba(255,255,255,.65);
  font-size: .78rem;
  letter-spacing: .04em;
  text-align: center;
  padding: .35rem 1rem;
}

/* NAVBAR */
.site-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--navy);
  box-shadow: var(--shadow-lg);
}

.nav-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 64px;
  position: relative;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: .75rem;
  text-decoration: none;
  flex-shrink: 0;
}

.brand-logo {
  width: 42px;
  height: 42px;
  object-fit: contain;
  flex-shrink: 0;
}

.brand-text { display: flex; flex-direction: column; line-height: 1.2; }
.brand-text strong { font-size: 1rem; font-weight: 700; color: var(--white); letter-spacing: .02em; }
.brand-text span { font-size: .72rem; color: rgba(255,255,255,.6); letter-spacing: .04em; text-transform: uppercase; }

nav { display: flex; align-items: center; gap: .25rem; }

nav a {
  color: rgba(255,255,255,.8);
  text-decoration: none;
  font-size: .875rem;
  font-weight: 500;
  padding: .45rem .85rem;
  border-radius: var(--radius);
  transition: background .18s, color .18s;
  white-space: nowrap;
}
nav a:hover { background: rgba(255,255,255,.12); color: var(--white); }
nav a.active { background: rgba(255,255,255,.16); color: var(--white); }

.nav-toggle {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: .5rem;
  flex-direction: column;
  gap: 5px;
}
.nav-toggle span { display: block; width: 22px; height: 2px; background: rgba(255,255,255,.8); border-radius: 2px; }

/* HERO */
.hero {
  background: linear-gradient(135deg, var(--navy-dark) 0%, var(--navy-mid) 100%);
  color: var(--white);
  padding: 4.5rem 1.5rem 5rem;
  text-align: center;
  position: relative;
  overflow: hidden;
}
.hero::after {
  content: "";
  position: absolute;
  bottom: -2px; left: 0; right: 0;
  height: 48px;
  background: var(--gray-50);
  clip-path: ellipse(55% 100% at 50% 100%);
}

.hero-badge {
  display: inline-block;
  background: rgba(200,151,43,.2);
  color: #f0c060;
  border: 1px solid rgba(200,151,43,.4);
  font-size: .75rem;
  font-weight: 600;
  letter-spacing: .08em;
  text-transform: uppercase;
  padding: .3rem .85rem;
  border-radius: 100px;
  margin-bottom: 1.25rem;
}

.hero h1 {
  font-size: clamp(1.6rem, 4vw, 2.5rem);
  font-weight: 800;
  letter-spacing: -.02em;
  line-height: 1.2;
  margin-bottom: .75rem;
}

.hero p { font-size: 1.05rem; color: rgba(255,255,255,.72); max-width: 540px; margin: 0 auto; }

/* MAIN */
main {
  max-width: 860px;
  margin: 0 auto;
  padding: 3rem 1.5rem 5rem;
  display: flex;
  flex-direction: column;
  gap: 4rem;
}

/* SECTION HEADER */
.section-header { display: flex; align-items: center; gap: .85rem; margin-bottom: 1.75rem; }

.section-icon {
  width: 44px; height: 44px;
  background: var(--accent-lt);
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.section-icon svg { width: 22px; height: 22px; stroke: var(--accent); }

.section-header h2 { font-size: 1.45rem; font-weight: 700; color: var(--navy); letter-spacing: -.01em; }

.section-divider { width: 100%; height: 1px; background: var(--gray-200); margin-bottom: 1.75rem; }

/* CARD */
.card {
  background: var(--white);
  border: 1px solid var(--gray-200);
  border-radius: 12px;
  padding: 2rem 2.25rem;
  box-shadow: var(--shadow);
}
.card p { color: var(--gray-600); margin-bottom: 1rem; }

/* CONTACT STRIP */
.contact-strip {
  display: flex; align-items: center; gap: .75rem;
  background: var(--accent-lt);
  border: 1px solid #bfdbfe;
  border-radius: var(--radius);
  padding: .85rem 1.1rem;
  margin: 1.25rem 0 1.75rem;
  font-size: .9rem;
  color: #1e40af;
}
.contact-strip svg { width: 18px; height: 18px; stroke: #2563eb; flex-shrink: 0; }
.contact-strip a { color: var(--accent); font-weight: 600; text-decoration: none; }
.contact-strip a:hover { text-decoration: underline; }

/* FORM */
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.form-group { display: flex; flex-direction: column; gap: .35rem; }
.form-group.full { grid-column: 1 / -1; }

label { font-size: .82rem; font-weight: 600; color: var(--gray-600); letter-spacing: .02em; text-transform: uppercase; }

input, textarea {
  font-family: inherit;
  font-size: .95rem;
  color: var(--gray-800);
  background: var(--gray-50);
  border: 1.5px solid var(--gray-200);
  border-radius: var(--radius);
  padding: .65rem .9rem;
  outline: none;
  transition: border-color .18s, box-shadow .18s;
  width: 100%;
}
input:focus, textarea:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(37,99,235,.12);
  background: var(--white);
}
textarea { resize: vertical; min-height: 130px; }

.btn-submit {
  display: inline-flex; align-items: center; gap: .5rem;
  background: var(--navy);
  color: var(--white);
  border: none;
  border-radius: var(--radius);
  padding: .75rem 1.75rem;
  font-family: inherit; font-size: .95rem; font-weight: 600;
  cursor: pointer;
  transition: background .18s, transform .1s;
  margin-top: .5rem;
}
.alert-success-strip, .alert-error-strip {
  display: flex; align-items: center; gap: .65rem;
  border-radius: var(--radius);
  padding: .85rem 1.1rem;
  margin-bottom: 1.25rem;
  font-size: .9rem;
}
.alert-success-strip { background: #dcfce7; border: 1px solid #86efac; color: #166534; }
.alert-error-strip   { background: #fee2e2; border: 1px solid #fca5a5; color: #991b1b; }
.alert-success-strip svg, .alert-error-strip svg { width: 18px; height: 18px; stroke: currentColor; flex-shrink: 0; }

@keyframes spin { to { transform: rotate(360deg); } }
.spin { animation: spin .8s linear infinite; }

.btn-submit:hover:not(:disabled) { background: var(--navy-mid); }
.btn-submit:active { transform: translateY(1px); }
.btn-submit svg { width: 16px; height: 16px; stroke: currentColor; }

/* PROSE */
.prose { font-size: .95rem; color: var(--gray-600); }
.prose p { color: var(--gray-600); margin-bottom: 1rem; }
.prose h3 { font-size: 1rem; font-weight: 700; color: var(--navy); margin-top: 1.75rem; margin-bottom: .5rem; }
.prose h3:first-child { margin-top: 0; }
.prose ul, .prose ol { padding-left: 1.4rem; margin-bottom: 1rem; }
.prose li { margin-bottom: .35rem; }
.prose strong { color: var(--gray-800); }
.prose a { color: var(--accent); }

.effective-date {
  display: inline-block;
  font-size: .8rem; font-weight: 600;
  color: var(--gray-400);
  letter-spacing: .04em; text-transform: uppercase;
  margin-bottom: 1.25rem;
  background: var(--gray-100);
  padding: .25rem .7rem;
  border-radius: 100px;
}

/* FOOTER */
footer {
  background: var(--navy-dark);
  color: rgba(255,255,255,.55);
  padding: 2.5rem 1.5rem;
  text-align: center;
  font-size: .85rem;
  line-height: 1.8;
}
.footer-logo { font-size: 1rem; font-weight: 700; color: rgba(255,255,255,.85); margin-bottom: .25rem; }
footer a { color: rgba(255,255,255,.65); text-decoration: none; }
footer a:hover { color: var(--white); text-decoration: underline; }
.footer-divider { margin: .75rem auto; width: 40px; height: 1px; background: rgba(255,255,255,.15); }
.footer-email { margin-top: .35rem; }
.footer-links { margin-top: .75rem; display: flex; justify-content: center; gap: 1.5rem; font-size: .8rem; }

/* RESPONSIVE */
@media (max-width: 680px) {
  .nav-toggle { display: flex; }
  nav {
    display: none; flex-direction: column;
    position: absolute; top: 64px; left: 0; right: 0;
    background: var(--navy-dark);
    padding: .75rem 1rem 1rem;
    border-top: 1px solid rgba(255,255,255,.1);
  }
  nav.open { display: flex; }
  nav a { padding: .65rem 1rem; }
  .form-grid { grid-template-columns: 1fr; }
  .card { padding: 1.5rem 1.25rem; }
  main { padding: 2rem 1rem 4rem; }
}
</style>
